import React, { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  PlatformSiteHealthHistory,
  SiteHealthHistoryItem,
  getSiteHealthHistory,
} from '../../api/platform/system';
import './SiteUptimeHistory.css';

const formatPercent = (value: number | null) => (value == null ? '—' : `${value.toFixed(value >= 99 ? 2 : 1)}%`);

const formatTick = (value: string) => {
  const date = new Date(value);
  return new Intl.DateTimeFormat(undefined, { weekday: 'short', hour: 'numeric' }).format(date);
};

const SiteCard: React.FC<{ site: SiteHealthHistoryItem }> = ({ site }) => (
  <article className="uptime-card">
    <div className="uptime-card__header">
      <div>
        <div className="uptime-card__title-row">
          <span className={`uptime-status uptime-status--${site.latestStatus || 'unknown'}`} />
          <strong>{site.name}</strong>
        </div>
        <span>{site.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
      </div>
      <div className="uptime-card__uptime">
        <strong>{formatPercent(site.uptime.last7Days)}</strong>
        <span>7-day uptime</span>
      </div>
    </div>

    <div className="uptime-card__metrics">
      <div><span>24h</span><strong>{formatPercent(site.uptime.last24Hours)}</strong></div>
      <div><span>7d</span><strong>{formatPercent(site.uptime.last7Days)}</strong></div>
      <div><span>30d</span><strong>{formatPercent(site.uptime.last30Days)}</strong></div>
    </div>

    <div className="uptime-card__chart">
      {site.series.length ? (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={site.series} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#edf2f6" />
            <XAxis dataKey="checkedAt" tickFormatter={formatTick} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} minTickGap={34} />
            <YAxis yAxisId="latency" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={38} />
            <Tooltip
              labelFormatter={formatTick}
              formatter={(value: number | string, name: string) => [
                name === 'Latency' ? `${value} ms` : `${value}%`,
                name,
              ]}
            />
            <Line yAxisId="latency" type="monotone" dataKey="averageResponseTimeMs" name="Latency" stroke="#246bff" strokeWidth={2} dot={false} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="uptime-card__empty">History begins collecting after deployment.</div>
      )}
    </div>
  </article>
);

const SiteUptimeHistory: React.FC = () => {
  const [history, setHistory] = useState<PlatformSiteHealthHistory | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    getSiteHealthHistory(7)
      .then(setHistory)
      .catch(() => setError(true));
  }, []);

  const hasSamples = useMemo(
    () => history?.sites.some((site) => site.series.length > 0) || false,
    [history]
  );

  return (
    <section className="uptime-history">
      <div className="uptime-history__heading">
        <div>
          <span>Historical monitoring</span>
          <h2>Uptime & Latency</h2>
          <p>Five-minute checks roll into hourly history and 24-hour, 7-day, and 30-day uptime.</p>
        </div>
        <div className="uptime-history__window">7-day graph</div>
      </div>

      {error ? (
        <div className="uptime-history__message">Historical uptime will appear once the monitoring service is deployed.</div>
      ) : !history ? (
        <div className="uptime-history__message">Loading uptime history…</div>
      ) : (
        <>
          {!hasSamples ? <div className="uptime-history__notice">Monitoring is enabled; the charts will fill as five-minute checks accumulate.</div> : null}
          <div className="uptime-history__grid">
            {history.sites.map((site) => <SiteCard key={site.key} site={site} />)}
          </div>
        </>
      )}
    </section>
  );
};

export default SiteUptimeHistory;
